// Types for structured data
interface SnippetData {
  keywords: {
    missing: string[];
    present: string[];
    seo: string[];
  };
  headers: string[];
  text: string;
  result: string;
  description: string;
}

// Global state to store snippet data
let snippetData: SnippetData = {
  keywords: {
    missing: [],
    present: [],
    seo: []
  },
  headers: [],
  text: '',
  result: '',
  description: ''
};

// Function to update snippet data
export function updateSnippetData(data: Partial<SnippetData>) {
  console.log('🟡 UPDATE SNIPPET DATA - Updating snippet data:', data);
  
  // Deep merge for keywords
  if (data.keywords) {
    snippetData.keywords = {
      ...snippetData.keywords,
      ...data.keywords
    };
    console.log('🟡 UPDATE SNIPPET DATA - Updated keywords:', snippetData.keywords);
  }

  // Update other fields if provided
  if (data.headers) {
    snippetData.headers = data.headers;
  }
  if (data.text !== undefined) {
    snippetData.text = data.text;
  }
  if (data.result !== undefined) {
    snippetData.result = data.result;
  }
  if (data.description !== undefined) {
    snippetData.description = data.description;
  }

  // Log updated state
  console.log('🟡 UPDATE SNIPPET DATA - Updated snippet data:', snippetData);
}

// Function to get formatted keyword lists
function getFormattedKeywords(keywords: string[], type: string): string {
  if (!keywords || keywords.length === 0) {
    return `No ${type} keywords available`;
  }

  // Filter out empty strings and duplicates
  const uniqueKeywords = [...new Set(keywords.filter(k => k.trim()))];
  
  // Format based on type
  switch (type) {
    case 'missing':
      return `Missing Keywords (${uniqueKeywords.length}): ${uniqueKeywords.join(', ')}`;
    case 'present':
      return `Present Keywords (${uniqueKeywords.length}): ${uniqueKeywords.join(', ')}`;
    case 'seo':
      return `SEO Keywords (${uniqueKeywords.length}): ${uniqueKeywords.join(', ')}`;
    default:
      return uniqueKeywords.join(', ');
  }
}

// Function to get formatted headers
function getFormattedHeaders(headers: string[]): string {
  if (!headers || headers.length === 0) {
    return 'No competitor headers available';
  }

  return headers
    .filter(h => h.trim())
    .map(h => `- ${h}`)
    .join('\n');
}

export function replaceSnippets(prompt: string): string {
  try {
    console.log('🔵 REPLACE SNIPPETS - Replacing snippets in prompt:', prompt);
    console.log('🔵 REPLACE SNIPPETS - Current snippet data:', snippetData);
    console.log('🔵 REPLACE SNIPPETS - Missing keywords:', snippetData.keywords.missing);
    console.log('🔵 REPLACE SNIPPETS - Present keywords:', snippetData.keywords.present);

    // Create replacement map
    const replacements: Record<string, string> = {
      '{{Missing words}}': getFormattedKeywords(snippetData.keywords.missing, 'missing'),
      '{{Present words}}': getFormattedKeywords(snippetData.keywords.present, 'present'),
      '{{Keywords}}': getFormattedKeywords(snippetData.keywords.seo, 'seo'),
      '{{Headers}}': getFormattedHeaders(snippetData.headers),
      '{{Text}}': snippetData.text || 'No content available',
      '{{Resultai}}': snippetData.result || 'No result available',
      '{{Description}}': snippetData.description || 'No description available'
    };

    console.log('🔵 REPLACE SNIPPETS - Replacements:', replacements);

    // Replace all snippets
    let processedPrompt = prompt;
    Object.entries(replacements).forEach(([placeholder, value]) => {
      const regex = new RegExp(escapeRegExp(placeholder), 'g');
      processedPrompt = processedPrompt.replace(regex, value);
    });

    console.log('🔵 REPLACE SNIPPETS - Processed prompt:', processedPrompt);
    return processedPrompt;

  } catch (error) {
    console.error('Error replacing snippets:', error);
    return prompt;
  }
}

// Helper function to escape special characters in regex
function escapeRegExp(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}